import { useCallback, useSyncExternalStore } from 'react';
import { useMediaQuery } from './useMediaQuery';
import {
  APP_NAV_RAIL_QUERIES,
  readSidebarPinned,
  railWidthForBand,
  setSidebarPinned,
  subscribeSidebarPinned,
  type AppNavRailBand,
} from '../ui/appNavRail';

export interface UseAppNavRailResult {
  band: AppNavRailBand;
  pinned: boolean;
  /** The 64px icon rail is showing (rail band, not pinned). */
  railed: boolean;
  /** The in-flow width the rail takes from the page: 0 / 64 / 240. */
  width: 0 | 64 | 240;
  togglePinned: () => void;
}

/**
 * Story 24.6 — the live app-nav rail state. The band comes from the same
 * media queries as Tailwind's `lg` / `xl`, so jsdom's all-false `matchMedia`
 * stub reads as `off-canvas` (rail tests must stub the queries). The pin is
 * the shared store in `src/lib/ui/appNavRail.ts`, so the Sidebar and the toast
 * agree on every render.
 */
export function useAppNavRail(): UseAppNavRailResult {
  const isFull = useMediaQuery(APP_NAV_RAIL_QUERIES.full);
  const isRail = useMediaQuery(APP_NAV_RAIL_QUERIES.rail);
  const pinned = useSyncExternalStore(subscribeSidebarPinned, readSidebarPinned, () => false);
  const band: AppNavRailBand = isFull ? 'full' : isRail ? 'rail' : 'off-canvas';
  const togglePinned = useCallback(() => setSidebarPinned(!readSidebarPinned()), []);
  return {
    band,
    pinned,
    railed: band === 'rail' && !pinned,
    width: railWidthForBand(band, pinned),
    togglePinned,
  };
}
